import { useState } from "react";
import {
  NotAuthenticatedError,
  api,
  type CalendarStatus,
  type KillSwitch,
  type KillSwitchAccount,
  type KillSwitchLatch,
} from "../api";
import { useApiData } from "../useApi";
import { PageState, formatTime } from "../components";

/**
 * Risk Center: the page an operator reaches for when something is wrong.
 *
 * Two design rules it follows, both from docs/07 §25:
 *
 * 1. **Stopping is one click; releasing is not.** Engaging a level needs a
 *    reason and nothing else. Clearing a latched trigger is per-trigger, needs
 *    a reason, and there is no "clear all" — dropping every automatic halt at
 *    once is the mistake the kill switch exists to prevent.
 * 2. **What the server says, not what the UI works out.** `signals_allowed`,
 *    `new_entries_allowed` and `execution_allowed` are read straight from the
 *    response rather than derived from the level here. A second implementation
 *    of that mapping in TypeScript is a second thing that can be wrong about
 *    whether the system is trading.
 */
export function RiskCenter({
  onSignedOut,
  canEngage,
  canRelease,
}: {
  onSignedOut: () => void;
  canEngage: boolean;
  canRelease: boolean;
}) {
  const { data, error, loading, reload } = useApiData<KillSwitch>(
    () => api.killSwitch(),
    onSignedOut,
  );

  return (
    <>
      <h1>Risk Center</h1>
      <p className="subtitle">
        Kill switch state, and the halt controls.{" "}
        <button className="link" onClick={reload}>
          Refresh
        </button>
      </p>

      <PageState
        loading={loading}
        error={error}
        empty={data?.accounts.length === 0}
        emptyMessage="No EA accounts are configured."
      >
        {data?.accounts.map((account) => (
          <AccountPanel
            key={account.account_ref}
            account={account}
            levels={data.levels}
            canEngage={canEngage}
            canRelease={canRelease}
            onChanged={reload}
            onSignedOut={onSignedOut}
          />
        ))}
      </PageState>

      <CalendarPanel onSignedOut={onSignedOut} />
    </>
  );
}

/**
 * The economic calendar's health, which is a Risk Center concern because a
 * calendar that cannot be trusted latches `STALE_NEWS_DATA` and stops new
 * entries.
 *
 * The row that matters is `last_error` while `staleness_problem` is still
 * null: refreshes failing, cache still inside its age limit, nothing halted.
 * That is the window to act in, and it was invisible until this panel —
 * recorded in the database and read by nobody (docs/07 §35).
 *
 * It does not group events by symbol. Which release gates which instrument is
 * the Risk Engine's blackout check, and a second copy of that rule in the UI
 * would be a second thing that can disagree about whether trading is stopped.
 */
function CalendarPanel({ onSignedOut }: { onSignedOut: () => void }) {
  const { data, error, loading } = useApiData<CalendarStatus>(
    () => api.calendar(),
    onSignedOut,
  );

  if (loading || error || !data) {
    return (
      <div className="card">
        <h2>Economic calendar</h2>
        <PageState loading={loading} error={error} empty={false} emptyMessage="">
          {null}
        </PageState>
      </div>
    );
  }

  const stale = data.staleness_problem !== null;
  const failingButUsable = !stale && data.state?.last_error;

  return (
    <div className="card">
      <h2>
        Economic calendar{" "}
        <span className={stale ? "badge danger" : "badge ok"}>
          {stale ? "NOT TRUSTED" : "current"}
        </span>
      </h2>

      {stale && (
        <div className="notice error">
          {data.staleness_problem} — <strong>STALE_NEWS_DATA</strong> stops new
          entries until a refresh succeeds and the latch is cleared.
        </div>
      )}

      {failingButUsable && (
        <div className="notice">
          Refreshes are failing but the cached calendar is still in date —{" "}
          <strong>act now, before it goes stale</strong>:{" "}
          {data.state?.last_error}
          {data.state?.last_error_at
            ? ` (last tried ${formatTime(data.state.last_error_at)})`
            : ""}
        </div>
      )}

      {data.state ? (
        <div className="metrics">
          <div className="metric">
            <span className="label">Fetched</span>
            <span className="value">
              {formatTime(data.state.fetched_at)}
            </span>
          </div>
          <div className="metric">
            <span className="label">Age</span>
            <span className="value">
              {data.state.age_hours}h / {data.max_age_hours}h
            </span>
          </div>
          <div className="metric">
            <span className="label">Events</span>
            <span className="value">{data.state.event_count}</span>
          </div>
        </div>
      ) : (
        <p className="dim">The calendar has never been fetched.</p>
      )}

      {data.upcoming.length > 0 ? (
        <table>
          <thead>
            <tr>
              <th>When (UTC)</th>
              <th>Currency</th>
              <th>Event</th>
              <th>Forecast</th>
              <th>Previous</th>
            </tr>
          </thead>
          <tbody>
            {data.upcoming.map((event) => (
              <tr key={`${event.currency}-${event.scheduled_at}-${event.title}`}>
                <td>{formatTime(event.scheduled_at)}</td>
                <td>{event.currency}</td>
                <td>{event.title}</td>
                <td>{event.forecast || "—"}</td>
                <td>{event.previous || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="dim">
          No high-impact releases in the next 48 hours.
          {!stale && " The calendar is current, so this is a quiet period."}
        </p>
      )}
    </div>
  );
}

function AccountPanel({
  account,
  levels,
  canEngage,
  canRelease,
  onChanged,
  onSignedOut,
}: {
  account: KillSwitchAccount;
  levels: Array<{ level: number; name: string }>;
  canEngage: boolean;
  canRelease: boolean;
  onChanged: () => void;
  onSignedOut: () => void;
}) {
  const halted = account.level > 0;

  return (
    <div className="card">
      <h2>
        {account.account_ref}{" "}
        <span className={halted ? "badge danger" : "badge ok"}>
          {halted ? `HALTED — ${account.level_name}` : "running"}
        </span>
      </h2>

      <div className="metrics">
        <Gate label="Signals" allowed={account.signals_allowed} />
        <Gate label="New entries" allowed={account.new_entries_allowed} />
        <Gate label="Execution" allowed={account.execution_allowed} />
      </div>

      {account.manual_level > 0 && (
        <div className="notice">
          Engaged by a person at <strong>{account.manual_level_name}</strong>
          {account.manual_reason ? ` — ${account.manual_reason}` : ""}
        </div>
      )}

      {account.halted_strategies.length > 0 && (
        <p className="dim">
          Paused strategies: {account.halted_strategies.join(", ")}
        </p>
      )}

      <Latches
        account={account}
        canRelease={canRelease}
        onChanged={onChanged}
        onSignedOut={onSignedOut}
      />

      {canEngage ? (
        <EngageForm
          account={account}
          levels={levels}
          onChanged={onChanged}
          onSignedOut={onSignedOut}
        />
      ) : (
        <p className="dim">
          Your role can see the kill switch but not operate it.
        </p>
      )}

      <History accountRef={account.account_ref} onSignedOut={onSignedOut} />
    </div>
  );
}

function Gate({ label, allowed }: { label: string; allowed: boolean }) {
  return (
    <div className="metric">
      <div className="label">{label}</div>
      <div className="value">
        <span className={allowed ? "badge ok" : "badge danger"}>
          {allowed ? "allowed" : "stopped"}
        </span>
      </div>
    </div>
  );
}

function Latches({
  account,
  canRelease,
  onChanged,
  onSignedOut,
}: {
  account: KillSwitchAccount;
  canRelease: boolean;
  onChanged: () => void;
  onSignedOut: () => void;
}) {
  if (account.latches.length === 0) {
    return <p className="dim">No automatic trigger is latched.</p>;
  }

  return (
    <table>
      <thead>
        <tr>
          <th>Trigger</th>
          <th>Level</th>
          <th>Since</th>
          <th>Last seen</th>
          <th>Why</th>
          {canRelease && <th>Release</th>}
        </tr>
      </thead>
      <tbody>
        {account.latches.map((latch) => (
          <tr key={latch.trigger_code}>
            <td>
              <span className="badge danger">{latch.trigger_code}</span>
            </td>
            <td>{latch.level}</td>
            <td title={formatTime(latch.first_latched_at)}>
              {formatTime(latch.first_latched_at)}
            </td>
            <td title={formatTime(latch.last_observed_at)}>
              {formatTime(latch.last_observed_at)}
            </td>
            <td className="dim">{latch.detail}</td>
            {canRelease && (
              <td>
                <ReleaseButton
                  accountRef={account.account_ref}
                  latch={latch}
                  onChanged={onChanged}
                  onSignedOut={onSignedOut}
                />
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ReleaseButton({
  accountRef,
  latch,
  onChanged,
  onSignedOut,
}: {
  accountRef: string;
  latch: KillSwitchLatch;
  onChanged: () => void;
  onSignedOut: () => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function release() {
    setBusy(true);
    setMessage(null);
    try {
      const result = await api.clearKillSwitchTrigger(
        accountRef,
        latch.trigger_code,
        reason,
      );
      // `released: false` means nothing was latched. Saying so matters: an
      // operator who reads "cleared" when the state never changed will believe
      // the system is free when it is not.
      setMessage(
        result.released
          ? null
          : "Nothing was latched for that trigger — state unchanged.",
      );
      setReason("");
      onChanged();
    } catch (err) {
      if (err instanceof NotAuthenticatedError) {
        onSignedOut();
        return;
      }
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="reason (required)"
        aria-label={`Reason for releasing ${latch.trigger_code}`}
      />
      <button disabled={busy || reason.trim() === ""} onClick={release}>
        {busy ? "Releasing…" : "Release"}
      </button>
      {message && <div className="notice error">{message}</div>}
      <p className="dim">
        Releasing asserts the cause was dealt with. It is not a suppression —
        the trigger latches again on the next evaluation if the condition still
        holds.
      </p>
    </>
  );
}

function EngageForm({
  account,
  levels,
  onChanged,
  onSignedOut,
}: {
  account: KillSwitchAccount;
  levels: Array<{ level: number; name: string }>;
  onChanged: () => void;
  onSignedOut: () => void;
}) {
  const [level, setLevel] = useState(account.manual_level);
  const [reason, setReason] = useState("");
  const [strategies, setStrategies] = useState(
    account.halted_strategies.join(", "),
  );
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setMessage(null);
    try {
      await api.engageKillSwitch(
        account.account_ref,
        level,
        reason,
        strategies
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s !== ""),
      );
      setReason("");
      onChanged();
    } catch (err) {
      if (err instanceof NotAuthenticatedError) {
        onSignedOut();
        return;
      }
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      <h3>Set the manual level</h3>
      <label>
        Level
        <select
          value={level}
          onChange={(e) => setLevel(Number(e.target.value))}
        >
          {levels.map((option) => (
            <option key={option.level} value={option.level}>
              {option.level} — {option.name}
            </option>
          ))}
        </select>
      </label>

      {level === 1 && (
        <label>
          Strategies to pause (comma separated)
          <input
            value={strategies}
            onChange={(e) => setStrategies(e.target.value)}
            placeholder="donchian_atr_v1"
          />
        </label>
      )}

      <label>
        Reason
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="required — an unexplained halt is one nobody can safely release"
        />
      </label>

      <button
        className={level > 0 ? "danger" : undefined}
        disabled={busy || reason.trim() === ""}
        onClick={submit}
      >
        {busy ? "Applying…" : level === 0 ? "Release manual halt" : "Halt"}
      </button>

      {message && <div className="notice error">{message}</div>}

      <p className="dim">
        Level 0 releases only the manual engagement. Latched automatic triggers
        stay engaged and are released one at a time above.
      </p>
    </div>
  );
}

function History({
  accountRef,
  onSignedOut,
}: {
  accountRef: string;
  onSignedOut: () => void;
}) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button className="link" onClick={() => setOpen(true)}>
        Show halt history
      </button>
    );
  }

  return <HistoryTable accountRef={accountRef} onSignedOut={onSignedOut} />;
}

function HistoryTable({
  accountRef,
  onSignedOut,
}: {
  accountRef: string;
  onSignedOut: () => void;
}) {
  const { data, error, loading } = useApiData(
    () => api.killSwitchHistory(accountRef),
    onSignedOut,
    [accountRef],
  );

  return (
    <>
      <h3>Halt history</h3>
      <PageState
        loading={loading}
        error={error}
        empty={data?.latches.length === 0}
        emptyMessage="This account has never been halted by a trigger."
      >
        <table>
          <thead>
            <tr>
              <th>Trigger</th>
              <th>Latched</th>
              <th>Why</th>
              <th>Released</th>
              <th>By</th>
              <th>Release reason</th>
            </tr>
          </thead>
          <tbody>
            {data?.latches.map((latch) => (
              <tr key={`${latch.trigger_code}-${latch.first_latched_at}`}>
                <td>{latch.trigger_code}</td>
                <td>{formatTime(latch.first_latched_at)}</td>
                <td className="dim">{latch.detail}</td>
                <td>
                  {latch.cleared_at ? (
                    formatTime(latch.cleared_at)
                  ) : (
                    <span className="badge danger">still engaged</span>
                  )}
                </td>
                <td>{latch.cleared_by ?? "—"}</td>
                <td className="dim">{latch.cleared_reason ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </PageState>
    </>
  );
}
